/* global HTMLIonTextareaElement, HTMLIonModalElement */
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonDatetime,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonText,
  IonTextarea,
  IonTitle,
  IonToolbar,
  useIonModal,
} from '@ionic/react';
import { captureException } from '@sentry/react';
import Form, { FormInstance, useForm, useWatch } from 'rc-field-form';
import { StoreValue } from 'rc-field-form/es/interface';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { t } from 'ttag';

import Field from '@components/Form/Field';
import InputField from '@components/Form/InputField';
import RouterPrompt from '@components/RouterPrompt';
import useToast from '@hooks/useToast';
import { formatDateMonthLong, formatTime } from '@services/date-service';
import { Locale } from '@services/language-service';
import { getLocale } from '@state/system';
import { getPeopleId as getUserId } from '@state/user';

import Rating from './Rating';
import { CustomField, CustomFieldConfig, FieldOption, FieldType, Lead, Payload } from './service';
import './style.css';

const MAX_ADDITIONAL_NOTE_CHARS = 500;

export enum Mode {
  View,
  Edit,
  Add,
  QualifyLead,
}

const isViewMode = (mode: Mode) => mode === Mode.View;
const isEditMode = (mode: Mode) => mode === Mode.Edit;
const isAddMode = (mode: Mode) => mode === Mode.Add;
const isQualifyLeadMode = (mode: Mode) => mode === Mode.QualifyLead;

const isFieldTypeRating = (type: FieldType) => type === FieldType.Rating;
const isFieldTypeText = (type: FieldType) => type === FieldType.Text;
const isFieldTypeSingleSelect = (type: FieldType) => type === FieldType.SingleSelect;
const isFieldTypeMultiSelect = (type: FieldType) => type === FieldType.MultiSelect;
const isFieldTypeDateTime = (type: FieldType) => type === FieldType.DateTime;

const Title = styled.h3`
  font-weight: 700;
  margin-bottom: 0px;
`;

const FieldTitle = styled.h2`
  && {
    font-weight: 600;
  }
`;

const ButtonGroup = styled.div`
  padding: 40px 16px ${window.innerHeight >= 600 && window.innerHeight <= 900 ? '100px' : '0px'};
`;

const ActionButton = styled(IonButton)`
  width: 100%;
`;

const RatingAndTextQualifierItem = styled(IonItem)`
  ion-item {
    padding-left: 0px;
  }
  margin-bottom: 10px;
`;

const QualifierItem = styled.div`
  margin-bottom: 10px;
  width: 100%;
`;

const DateDisplay = styled(IonLabel)`
  display: 'inline-flex';
`;

const TextFieldItem = styled(IonItem)`
  width: 100%;
  padding: 0 16px;
  --padding-start: 0px;
`;

const EditTextButton = styled(IonButton)`
  --padding-start: 0;
  margin: 0;
`;

const DateTimeModal = styled(IonModal)`
  --width: fit-content;
  --min-width: 250px;
  --height: fit-content;
  --border-radius: 6px;
  --box-shadow: 0 28px 48px rgba(0, 0, 0, 0.4);
`;
const QualifierIonItem = styled(IonItem)`
  --padding-start: 0px;
  --inner-padding-end: 0px;
`;

type UserDetails = {
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  position?: string;
};

type TextModalProps = {
  getData: () => CustomField;
  onDismiss: (value: string) => void;
};

export type LeadDetails = Omit<Lead, 'id' | 'companyId' | 'updatedAt'>;

type LeadQualifierProps = {
  mode?: Mode;
  leadDetails?: LeadDetails;
  customFieldsConfig: CustomFieldConfig[];
  onTextFieldClick: (field: CustomFieldConfig) => void;
  getFieldValue: (name: string) => StoreValue;
};

type DetailsProps = {
  mode?: Mode;
  leadDetails?: LeadDetails;
  customFieldsConfig?: CustomFieldConfig[];
  getFormObject?: (form: FormInstance) => void;
  onSave?: (value: Payload) => Promise<void>;
  onOkNavigationAlert?: () => void;
};

const TextModal = ({ getData, onDismiss }: TextModalProps) => {
  const { name, value: initialValue } = getData();
  const [value, setValue] = useState(initialValue ?? '');
  const ref = useRef<HTMLIonTextareaElement>(null);

  useEffect(() => {
    // a delay is required in ionic for focus to work properly
    // this is also discussed here - https://github.com/ionic-team/ionic-framework/issues/22667
    setTimeout(() => {
      if (ref.current) {
        ref.current.setFocus();
      }
    }, 600);
  }, []);

  const handleOnChange = () => {
    if (ref.current?.value && ref.current?.value.length > MAX_ADDITIONAL_NOTE_CHARS) {
      // maxlength property of iontextarea is not working on physical device(Android and iOS both)
      // it although works on browser, this issues is also been reported here - https://github.com/ionic-team/ionic-framework/issues/11578
      // Solution: For now, using ref to directly mutate the input value
      ref.current.value = ref.current.value.slice(0, MAX_ADDITIONAL_NOTE_CHARS);
    }
    setValue(ref.current?.value ?? '');
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{name}</IonTitle>
          <IonButtons slot="end">
            <IonButton
              // skipcq: JS-0417
              onClick={() => onDismiss(value)}
            >
              {t`Done`}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <TextFieldItem>
          <IonTextarea
            ref={ref}
            value={value}
            data-testid="input-note"
            placeholder={t`Add text here`}
            rows={11}
            // skipcq: JS-0417
            onIonInput={handleOnChange}
          />
        </TextFieldItem>
        <IonItem lines="none">
          <IonText style={{ width: '100%', fontSize: '13px' }} className="ion-text-right">
            {value?.length}/{MAX_ADDITIONAL_NOTE_CHARS} {t`Characters`}
          </IonText>
        </IonItem>
      </IonContent>
    </IonPage>
  );
};

const formateDateTime = (rawValue: string | null, locale: Locale) => {
  if (!rawValue) {
    return null;
  }
  const date = formatDateMonthLong({ utcDate: rawValue, locale });
  const time = formatTime({
    utcDate: rawValue,
    locale,
    is24HourFormat: false,
  });

  return `${date} ${time}`;
};

interface DateTimeQualifierProps {
  label: string;
  locale: Locale;
  mode: Mode;
  value?: string | null;
  onIonChange?: (value: string | null) => void;
}

const DateTimeQualifier = ({
  label,
  locale,
  mode,
  value: dateValue = null,
  onIonChange,
}: DateTimeQualifierProps) => {
  const [value, setValue] = useState<string | null>(dateValue);
  const [formattedValue, setFormattedValue] = useState<string | null>(
    formateDateTime(dateValue, locale)
  );
  const modal = useRef<HTMLIonModalElement>(null);
  const [showModal, setShowModal] = useState(false);
  return (
    <QualifierItem>
      <IonLabel mode="ios" position="stacked">
        <IonText>
          <FieldTitle>{label}</FieldTitle>
        </IonText>
      </IonLabel>

      <QualifierIonItem>
        <DateDisplay>{formattedValue}</DateDisplay>
        <IonButton
          fill="clear"
          // skipcq: JS-0417
          onClick={() => {
            setShowModal(true);
          }}
          disabled={isViewMode(mode)}
        >
          {t`Change date`}
        </IonButton>
        <DateTimeModal
          // skipcq: JS-0417
          onDidDismiss={() => {
            setShowModal(false);
          }}
          ref={modal}
          isOpen={showModal}
        >
          <IonDatetime
            data-testid="date-time"
            id="datetime"
            presentation="date-time"
            value={value}
            // skipcq: JS-0417
            onIonChange={(e: CustomEvent) => {
              const rawValue = e.detail.value as string;
              if (!rawValue) {
                setShowModal(false);
              }

              setValue(rawValue);
              setFormattedValue(formateDateTime(rawValue, locale));
            }}
          />
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton
                // skipcq: JS-0417
                onClick={() => {
                  setShowModal(false);
                  if (value && onIonChange) {
                    onIonChange(value);
                  }
                }}
              >
                {t`Ok`}
              </IonButton>
            </IonButtons>
            <IonButtons slot="end">
              <IonButton
                color="danger"
                // skipcq: JS-0417
                onClick={() => {
                  setValue(null);
                  setFormattedValue(null);
                  if (onIonChange) onIonChange(null);
                  setShowModal(false);
                }}
              >
                {t`Reset`}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </DateTimeModal>
      </QualifierIonItem>
    </QualifierItem>
  );
};

interface TextQualifierProps {
  value?: string;
  mode: Mode;
}

const TextQualifier = ({ value, mode }: TextQualifierProps) => (
  <IonText color="medium" style={{ whiteSpace: 'pre-line', maxHeight: 300, overflow: 'scroll' }}>
    {
      isQualifyLeadMode(mode) || isAddMode(mode)
        ? value || t`Add text here` // skipcq: JS-W1043
        : value || 'N/A' // skipcq: JS-W1043
    }
  </IonText>
);

const onCheckboxChange = (
  event: CustomEvent,
  value?: string[],
  onIonChange?: (value: string[]) => void
) => {
  const {
    detail: { value: selectedOption, checked },
  } = event;

  if (!onIonChange) return;

  if (!value) {
    onIonChange(checked ? [selectedOption] : []);
    return;
  }

  if (checked) {
    onIonChange(value.concat(selectedOption));
    return;
  }

  onIonChange(value.filter(val => val !== selectedOption));
};

interface SelectQualifierProps {
  label: string;
  mode: Mode;
  options?: FieldOption[];
  multiSelect?: boolean;
  onIonChange?: (value: string[]) => void;
  getValue: () => { optionsValues: string[] };
  value?: { optionsValues: string[] };
}

const SelectQualifier = ({
  label,
  mode,
  options,
  multiSelect,
  onIonChange,
  // this function returns the latest selected values of a checkbox
  // we cannot rely on 'value' for the latest selection
  // because when the form.resetFields() is invoked
  // the checkbox onIonChange is triggered, and the 'value' gives the stale values
  getValue,
  value,
}: SelectQualifierProps) => (
  <QualifierItem>
    <IonLabel mode="ios" position="stacked" className="ion-text-wrap">
      <IonText>
        <FieldTitle>{label}</FieldTitle>
      </IonText>
    </IonLabel>
    <IonList>
      {!multiSelect && (
        <IonRadioGroup
          data-testid="radio-group"
          value={value?.optionsValues?.[0]}
          allowEmptySelection={false}
          // TODO: EXP-10650
          // skipcq: JS-0417
          onIonChange={({ detail: { value: selectedOption } }: CustomEvent) =>
            onIonChange?.(selectedOption ? [selectedOption] : [])
          }
        >
          {options?.map(option => (
            <QualifierIonItem key={option.id}>
              <IonRadio disabled={isViewMode(mode)} value={option.id}>
                {option.name}
              </IonRadio>
            </QualifierIonItem>
          ))}
        </IonRadioGroup>
      )}
      {multiSelect &&
        options?.map(option => (
          <QualifierIonItem key={option.id}>
            <IonCheckbox
              data-testid={`option-id-${option.id}`}
              disabled={isViewMode(mode)}
              value={option.id}
              checked={Boolean(value?.optionsValues?.find(val => val === option.id))}
              // TODO: EXP-10650
              // skipcq: JS-0417
              onIonChange={(e: CustomEvent) =>
                onCheckboxChange(e, getValue()?.optionsValues, onIonChange)
              }
              aria-label={option.name}
            >
              {option.name}
            </IonCheckbox>
          </QualifierIonItem>
        ))}
    </IonList>
  </QualifierItem>
);

const LeadQualifier = ({
  mode = Mode.Add,
  customFieldsConfig,
  leadDetails,
  onTextFieldClick,
  getFieldValue,
}: LeadQualifierProps) => {
  const locale = useSelector(getLocale);
  const userId = useSelector(getUserId);

  // When user opens a lead which is captured by another user, they shouldn't see lead qualifiers having no value.
  // If all the Lead Qualifiers were set without value, then Lead Qualifier heading shouldn't be shown as well, hence this logic.
  if (
    isViewMode(mode) &&
    leadDetails?.capturedByPerson?.id !== userId &&
    !leadDetails?.customFields.some(x => x.optionsValues || (x.value && x.value !== '0'))
  ) {
    return null;
  }

  const customFieldsFiltered = customFieldsConfig.filter(({ id }) => {
    const field = getFieldValue(id);
    if (isViewMode(mode) && leadDetails?.capturedByPerson?.id !== userId) {
      // When user opens a lead which is captured by another user, they shouldn't see lead qualifiers having no value.
      return field && (field?.optionsValues?.length || field !== '0');
    }
    return true;
  });

  return (
    <>
      <IonItem lines="none">
        <IonText>
          <Title>{t`Lead Qualifier`}</Title>
        </IonText>
      </IonItem>
      {customFieldsFiltered.map(({ id, name, type, options }) => (
        <React.Fragment key={id}>
          {isFieldTypeRating(type) && (
            <RatingAndTextQualifierItem lines="none">
              <IonLabel mode="ios" position="stacked" className="ion-text-wrap">
                <IonText>
                  <FieldTitle>{name}</FieldTitle>
                </IonText>
              </IonLabel>
              <Field name={id} rules={[{ required: false }]}>
                <Rating readonly={isViewMode(mode)} />
              </Field>
            </RatingAndTextQualifierItem>
          )}

          {isFieldTypeText(type) && (
            <RatingAndTextQualifierItem
              // skipcq: JS-0417
              onClick={() => !isViewMode(mode) && onTextFieldClick({ id, name, type })}
              lines="none"
            >
              <IonLabel mode="ios" position="stacked" className="ion-text-wrap">
                <IonText>
                  <FieldTitle>{name}</FieldTitle>
                </IonText>
              </IonLabel>
              <Field name={id} rules={[{ required: false }]} showItemBorder={false}>
                <TextQualifier mode={mode} />
              </Field>
              {isEditMode(mode) && (
                <EditTextButton size="small" fill="clear">{t`Edit text`}</EditTextButton>
              )}
            </RatingAndTextQualifierItem>
          )}
          {(isFieldTypeSingleSelect(type) || isFieldTypeMultiSelect(type)) && (
            <Field
              name={id}
              rules={[{ required: false }]}
              showItemBorder={false}
              // TODO: EXP-10650
              // skipcq: JS-0417
              // assigning null is important because the way 'onIonChange' is triggered
              normalize={optionsValues => (optionsValues.length > 0 ? { optionsValues } : null)}
            >
              <SelectQualifier
                mode={mode}
                label={name}
                // TODO: EXP-10650
                // skipcq: JS-0417
                getValue={() => getFieldValue(id)}
                options={options}
                multiSelect={isFieldTypeMultiSelect(type)}
              />
            </Field>
          )}

          {isFieldTypeDateTime(type) && (
            <Field name={id} rules={[{ required: false }]} showItemBorder={false}>
              <DateTimeQualifier mode={mode} label={name} locale={locale} />
            </Field>
          )}
        </React.Fragment>
      ))}
    </>
  );
};

const Details = ({
  leadDetails,
  customFieldsConfig = [],
  getFormObject,
  onSave,
  onOkNavigationAlert,
  mode = Mode.Add,
}: DetailsProps) => {
  const showToast = useToast();
  const [form] = useForm();
  const [shouldDisplayNavigationAlert, setShouldDisplayNavigationAlert] = useState(false);
  const [currentTextField, setCurrentTextField] = useState<CustomFieldConfig>();
  const fields = useWatch([], form);
  const initialValues = customFieldsConfig.reduce(
    (accum, field) => {
      const leadCustomField = leadDetails?.customFields?.find(({ id }) => id === field.id);
      return {
        ...accum,
        [field.id]: leadCustomField?.optionsValues
          ? { optionsValues: leadCustomField?.optionsValues }
          : leadCustomField?.value,
      };
    },
    {
      firstName: leadDetails?.firstName,
      lastName: leadDetails?.lastName,
      email: leadDetails?.email,
      company: leadDetails?.company,
      position: leadDetails?.position,
    }
  );

  useEffect(() => {
    if (getFormObject) {
      getFormObject(form);
    }
  }, [getFormObject, form]);

  useEffect(() => {
    if (fields && isQualifyLeadMode(mode)) {
      const customFieldsFilled = customFieldsConfig?.filter(({ id }) => fields[id]);
      setShouldDisplayNavigationAlert(customFieldsFilled.length > 0);
    }
  }, [fields, customFieldsConfig, mode]);

  const [showModal, hideModal] = useIonModal(TextModal, {
    getData: () => ({
      ...(currentTextField || {}),
      value: currentTextField && form.getFieldValue(currentTextField.id),
    }),
    onDismiss: (value: string) => {
      if (currentTextField) {
        form.setFieldsValue({ [currentTextField.id]: value });
      }
      hideModal();
    },
  });

  const onTextFieldClick = useCallback(
    (field: CustomFieldConfig) => {
      setCurrentTextField(field);
      showModal();
    },
    [showModal]
  );

  const onFinish = useCallback(
    async (values: UserDetails & { [key: string]: { optionsValues: string[] } | string }) => {
      setShouldDisplayNavigationAlert(false);
      if (onSave) {
        try {
          const { firstName, lastName, position, company, email, ...customFields } = values;
          const customFieldsPayload = Object.keys(customFields).map(key => {
            const config = customFieldsConfig.find(({ id }) => id === key);
            if (
              config &&
              (isFieldTypeSingleSelect(config.type) || isFieldTypeMultiSelect(config.type))
            ) {
              const optionsValues = (customFields[key] as { optionsValues: string[] })
                ?.optionsValues;
              // Please note, foundation requires us to explicitly send the 'optionsValues' as 'null'
              // when all of the options in the 'multi-select' field is unchecked
              return { id: key, optionsValues: optionsValues ?? null };
            }
            return {
              id: key,
              // Same goes for the text and datetime field
              // if the values are empty/reset, foundation expect us to send the 'null' value
              value: customFields[key] ? String(customFields[key]) : null,
            };
          });

          await onSave({
            firstName,
            lastName,
            position,
            company,
            email,
            customFields: customFieldsPayload,
          });
          form.resetFields();
        } catch (err) {
          showToast(t`Something went wrong. Please try again.`);
          captureException(err);
        }
      }
    },
    [form, onSave, showToast, customFieldsConfig]
  );

  const handleNavigationAlert = useCallback(() => {
    form.resetFields();
    if (onOkNavigationAlert) {
      onOkNavigationAlert();
    }
  }, [form, onOkNavigationAlert]);

  return (
    <IonContent data-testid="lead">
      <Form form={form} initialValues={initialValues} name="LeadDetailForm" onFinish={onFinish}>
        <Field name="firstName" rules={[{ required: true, message: t`This field is required` }]}>
          <InputField
            data-testid="input-first-name"
            readonly={isViewMode(mode)}
            label={t`First Name`}
            labelPlacement="fixed"
            required
            id="first-name"
          />
        </Field>
        <Field name="lastName" rules={[{ required: true, message: t`This field is required` }]}>
          <InputField
            data-testid="input-last-name"
            readonly={isViewMode(mode)}
            label={t`Last Name`}
            labelPlacement="fixed"
            required
            id="last-name"
          />
        </Field>
        <Field
          name="email"
          rules={[
            { required: true, message: t`This field is required` },
            { type: 'email', message: t`Invalid email address.` },
          ]}
        >
          <InputField
            data-testid="input-email"
            readonly={isViewMode(mode)}
            label={t`Email`}
            labelPlacement="fixed"
            required
            id="email"
          />
        </Field>
        <Field name="company" rules={[]}>
          <InputField
            data-testid="input-company"
            readonly={isViewMode(mode)}
            label={t`Company`}
            labelPlacement="fixed"
          />
        </Field>
        <Field name="position" rules={[]}>
          <InputField
            data-testid="input-position"
            readonly={isViewMode(mode)}
            label={t`Position`}
            labelPlacement="fixed"
          />
        </Field>
        {customFieldsConfig.length > 0 && (
          <LeadQualifier
            mode={mode}
            leadDetails={leadDetails}
            customFieldsConfig={customFieldsConfig}
            onTextFieldClick={onTextFieldClick}
            getFieldValue={form.getFieldValue}
          />
        )}
        <ButtonGroup>
          {(isQualifyLeadMode(mode) || isAddMode(mode)) && (
            <ActionButton type="submit">{t`Done`}</ActionButton>
          )}
        </ButtonGroup>
      </Form>
      <RouterPrompt
        header={t`Skip this page?`}
        subHeader={t`Any changes with Lead Qualifier will not be saved to the lead.`}
        when={shouldDisplayNavigationAlert}
        onOk={handleNavigationAlert}
      />
    </IonContent>
  );
};

export default Details;
